/**
 * Every `fim-photo` write: the Rolodex upload protocol, the llm-service label
 * read, and the election fields that pin a photo to a subject. Fusion reads
 * the elections at its next run; nothing here calls fim-service.
 */

import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import imageCompression from 'browser-image-compression'
import { type AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import type { FimSubjectRecord } from 'features/fim/types'
import { useGeckoPermissions } from 'features/permissions'
import { useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { z } from 'zod'

import { sha256OfBlob } from './contentHash'
import { finiteOrNull, isRecord, stringOrNull } from './parse'
import { photoClient, readPhotoCaptureMetadata } from './photoCapture'
import {
  type ElectionTarget,
  electionTarget,
  photoStatusFor,
  withElection,
  withHero,
  withoutElection,
} from './photoElections'
import { photoLocator } from './photoLocation'
import {
  FIM_ENTRY_PHOTO,
  type FimPhotoData,
  PHOTO_FILE_PATH,
  PHOTO_THUMBNAIL_PATH,
  type PhotoCaptureMetadata,
  type PhotoClient,
  type PhotoElection,
  photoElectionSchema,
  type PhotoLocation,
  type PhotoSource,
  type PhotoUploadedBy,
} from './types'

type RolodexEditActions = Pick<
  ReturnType<typeof useRolodexEdit>,
  'rolodexPatch' | 'rolodexDelete' | 'rolodexUploadFile'
>

export interface PhotoServiceDeps extends RolodexEditActions {
  axios: AuthedAxiosRequests
  rolodexUrl: string
  llmServiceUrl: string
  uploader: PhotoUploadedBy | null
  client: PhotoClient
  locate: () => Promise<PhotoLocation | null>
  hashFile: (file: Blob) => Promise<string>
}

const FULL_RES_OPTIONS = {
  maxSizeMB: 4,
  maxWidthOrHeight: 2560,
  useWebWorker: true,
  fileType: 'image/jpeg',
}

const THUMBNAIL_OPTIONS = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 320,
  useWebWorker: true,
  fileType: 'image/jpeg',
}

/**
 * Thrown when the upload protocol fails AFTER the entry exists: the entry is
 * left at `status: "pending_upload"` and the caller offers retry (same-path
 * re-upload is safe) or discard.
 */
export class PhotoUploadError extends Error {
  readonly photoEntryId: string

  constructor(photoEntryId: string, cause: unknown) {
    super(
      `Photo upload did not complete for ${photoEntryId}: ${
        cause instanceof Error ? cause.message : String(cause)
      }`,
      { cause }
    )
    this.name = 'PhotoUploadError'
    this.photoEntryId = photoEntryId
  }
}

interface PhotoProvenance {
  capture: PhotoCaptureMetadata
  location: PhotoLocation | null
  contentSha256: string
}

/** Site tag only; `fim-*` entries carry no organization tag. */
async function createPhotoEntry(
  deps: PhotoServiceDeps,
  siteId: string,
  source: PhotoSource,
  { capture, location, contentSha256 }: PhotoProvenance
): Promise<string> {
  const id = `${FIM_ENTRY_PHOTO}-${uuidv4()}`
  const data: FimPhotoData = {
    ...capture,
    location,
    content_sha256: contentSha256,
    site: siteId,
    status: 'pending_upload',
    primary_label: null,
    caption: null,
    source,
    chat_session_id: null,
    mime_type: 'image/jpeg',
    analysis: null,
    elections: [],
  }
  await deps.axios.put(`${deps.rolodexUrl}/api/v2/entries/`, [
    { id, type: FIM_ENTRY_PHOTO, data, tags: { site: siteId } },
  ])
  return id
}

export interface UploadedPhoto {
  photoId: string
  elections: PhotoElection[]
}

const photoEntriesSchema = z.array(
  z.object({
    entry: z.object({
      id: z.string().min(1),
      data: z.object({ elections: z.array(photoElectionSchema) }),
    }),
  })
)

function parsePhotoEntries(data: unknown) {
  const result = photoEntriesSchema.safeParse(data)
  if (!result.success)
    throw new Error(t`Could not load this photo. Please try again.`)
  return result.data
}

// Elections are full replacements. Serialize reads and writes within this browser
// so concurrent upload waiters cannot overwrite each other's pins.
const electionUpdates = new Map<string, Promise<void>>()

async function withCurrentElections(
  deps: PhotoServiceDeps,
  photoId: string,
  update: (elections: PhotoElection[]) => Promise<void>
): Promise<void> {
  const key = `${deps.rolodexUrl}\n${photoId}`
  const previous = electionUpdates.get(key) ?? Promise.resolve()
  const operation = previous.then(async () => {
    const response = await deps.axios.post<unknown>(
      `${deps.rolodexUrl}/api/v2/entries/load`,
      { ids: [photoId], type: [FIM_ENTRY_PHOTO], limit: 1 }
    )
    const entry = parsePhotoEntries(response.data)[0]?.entry
    if (entry?.id !== photoId) throw new Error(t`Photo is no longer available.`)
    return update(entry.data.elections)
  })
  const settled = operation.then(
    () => {},
    () => {}
  )
  electionUpdates.set(key, settled)
  try {
    return await operation
  } finally {
    if (electionUpdates.get(key) === settled) electionUpdates.delete(key)
  }
}

/**
 * An entry on the site holding the same bytes, if one landed: the re-select
 * or double tap attaches the existing photo instead of minting a twin.
 */
async function findPhotoByHash(
  deps: PhotoServiceDeps,
  siteId: string,
  contentSha256: string
): Promise<UploadedPhoto | null> {
  const res = await deps.axios.post<unknown>(
    `${deps.rolodexUrl}/api/v2/entries/load`,
    {
      type: [FIM_ENTRY_PHOTO],
      tags: { site: [siteId] },
      filters: [
        { field: 'data', key: 'content_sha256', op: '=', value: contentSha256 },
        { field: 'data', key: 'status', op: '!=', value: 'pending_upload' },
      ],
      limit: 1,
    }
  )
  const entry = parsePhotoEntries(res.data)[0]?.entry
  return entry ? { photoId: entry.id, elections: entry.data.elections } : null
}

async function patchPhotoData(
  deps: PhotoServiceDeps,
  photoEntryId: string,
  data: Partial<FimPhotoData>
): Promise<void> {
  await deps.rolodexPatch([{ id: photoEntryId, data }])
}

interface EncodedPhoto {
  fullRes: Blob
  thumbnail: Blob
}

/** Re-encode to JPEG: bakes EXIF orientation in and strips EXIF/GPS. */
async function encodePhoto(file: File): Promise<EncodedPhoto> {
  const fullRes = await imageCompression(file, FULL_RES_OPTIONS)
  try {
    return {
      fullRes,
      thumbnail: await imageCompression(file, THUMBNAIL_OPTIONS),
    }
  } catch {
    return { fullRes, thumbnail: fullRes }
  }
}

/**
 * The bytes are durable once both signed PUTs return. Rolodex registers the
 * FileRef from a GCS finalize event that can lag by minutes, so nothing here
 * waits on it; until it lands, signed URLs 404 and the tile shows its
 * placeholder.
 */
async function uploadPhotoFiles(
  deps: PhotoServiceDeps,
  photoEntryId: string,
  encoded: EncodedPhoto
): Promise<void> {
  await Promise.all([
    deps.rolodexUploadFile(photoEntryId, encoded.fullRes, PHOTO_FILE_PATH),
    deps.rolodexUploadFile(
      photoEntryId,
      encoded.thumbnail,
      PHOTO_THUMBNAIL_PATH
    ),
  ])
  await patchPhotoData(deps, photoEntryId, { status: 'unattached' })
}

const uploadsInFlight = new Map<string, Promise<UploadedPhoto>>()

/**
 * The full upload protocol. The file is decoded and re-encoded before the
 * entry exists, so an unreadable image fails with no entry left behind; a
 * failure after creation leaves the entry at `pending_upload` and the thrown
 * `PhotoUploadError` carries its id for retry/discard. Bytes the site already
 * holds resolve to that entry's id with no new upload.
 */
export async function uploadPhoto(
  deps: PhotoServiceDeps,
  siteId: string,
  source: PhotoSource,
  file: File
): Promise<UploadedPhoto> {
  const [capture, location, contentSha256, encoded] = await Promise.all([
    readPhotoCaptureMetadata(file, deps.uploader, deps.client),
    deps.locate(),
    deps.hashFile(file),
    encodePhoto(file),
  ])
  const key = `${siteId}\n${contentSha256}`
  const inFlight = uploadsInFlight.get(key)
  if (inFlight) return inFlight
  const upload = storePhoto(deps, siteId, source, {
    capture,
    location,
    contentSha256,
    encoded,
  })
  uploadsInFlight.set(key, upload)
  try {
    return await upload
  } finally {
    uploadsInFlight.delete(key)
  }
}

async function storePhoto(
  deps: PhotoServiceDeps,
  siteId: string,
  source: PhotoSource,
  { encoded, ...provenance }: PhotoProvenance & { encoded: EncodedPhoto }
): Promise<UploadedPhoto> {
  const existing = await findPhotoByHash(deps, siteId, provenance.contentSha256)
  if (existing) return existing
  const photoEntryId = await createPhotoEntry(deps, siteId, source, provenance)
  try {
    await uploadPhotoFiles(deps, photoEntryId, encoded)
  } catch (error) {
    throw new PhotoUploadError(photoEntryId, error)
  }
  return { photoId: photoEntryId, elections: [] }
}

/** The retry path against an existing entry; same-path re-upload overwrites safely. */
export async function retryPhotoUpload(
  deps: PhotoServiceDeps,
  photoEntryId: string,
  file: File
): Promise<void> {
  await uploadPhotoFiles(deps, photoEntryId, await encodePhoto(file))
}

/** Soft delete with files; fusion stops deriving its elections at the next run. */
export async function discardPhoto(
  deps: PhotoServiceDeps,
  photoEntryId: string
): Promise<void> {
  await deps.rolodexDelete(photoEntryId, true)
}

export interface PhotoElectionRequest {
  siteId: string
  subject: FimSubjectRecord
}

export interface AttachPhotoRequest extends PhotoElectionRequest {
  label: string
  equipmentKind: string
}

/** Elect the photo onto a subject; re-attaching is idempotent. */
export async function attachPhoto(
  deps: PhotoServiceDeps,
  photoEntryId: string,
  request: AttachPhotoRequest
): Promise<ElectionTarget> {
  const target = electionTarget(request.siteId, request.subject)
  if (!target) {
    throw new Error('This Subject has no alias to pin a photo on yet.')
  }
  await withCurrentElections(deps, photoEntryId, async (elections) => {
    await patchPhotoData(deps, photoEntryId, {
      primary_label: request.label.trim(),
      status: 'attached',
      elections: withElection(
        elections,
        target,
        request.subject.subjectUid,
        request.equipmentKind,
        new Date().toISOString()
      ),
    })
  })
  return target
}

async function removeElection(
  deps: PhotoServiceDeps,
  photoEntryId: string,
  request: PhotoElectionRequest,
  deleteIfUnattached: boolean
): Promise<void> {
  await withCurrentElections(deps, photoEntryId, async (current) => {
    const elections = withoutElection(
      current,
      electionTarget(request.siteId, request.subject),
      request.subject.subjectUid
    )
    if (deleteIfUnattached && elections.length === 0) {
      await discardPhoto(deps, photoEntryId)
    } else if (elections.length !== current.length) {
      await patchPhotoData(deps, photoEntryId, {
        status: photoStatusFor(elections),
        elections,
      })
    }
  })
}

export function detachPhoto(
  deps: PhotoServiceDeps,
  photoEntryId: string,
  request: PhotoElectionRequest
): Promise<void> {
  return removeElection(deps, photoEntryId, request, false)
}

export function removePhotoFromSubject(
  deps: PhotoServiceDeps,
  photoEntryId: string,
  request: PhotoElectionRequest
): Promise<void> {
  return removeElection(deps, photoEntryId, request, true)
}

export async function setHeroPhoto(
  deps: PhotoServiceDeps,
  photoEntryId: string,
  request: PhotoElectionRequest
): Promise<void> {
  const target = electionTarget(request.siteId, request.subject)
  await withCurrentElections(deps, photoEntryId, async (elections) => {
    if (
      !target ||
      !elections.some((e) => e.subject_identity_norm === target.norm)
    ) {
      throw new Error('This photo is not pinned to this Subject.')
    }
    await patchPhotoData(deps, photoEntryId, {
      elections: withHero(elections, target, new Date().toISOString()),
    })
  })
}

export async function setPhotoLabel(
  deps: PhotoServiceDeps,
  photoEntryId: string,
  label: string
): Promise<void> {
  await patchPhotoData(deps, photoEntryId, { primary_label: label })
}

export async function setPhotoCaption(
  deps: PhotoServiceDeps,
  photoEntryId: string,
  caption: string | null
): Promise<void> {
  await patchPhotoData(deps, photoEntryId, { caption })
}

const PHOTO_LABEL_PROMPT =
  'You are labeling a photo an operator took of plant equipment. Read any ' +
  'nameplate, tag plate, or stenciled identifier and return the most ' +
  'specific equipment label (the tag or name the plant would use for this ' +
  'exact item) with your confidence from 0 to 1.'

const PHOTO_LABEL_SCHEMA = {
  type: 'object',
  properties: {
    label: { type: 'string' },
    confidence: { type: 'number' },
  },
  required: ['label'],
}

function parsePhotoLabel(
  text: string
): { label: string; confidence: number | null } | null {
  try {
    const raw: unknown = JSON.parse(text)
    if (!isRecord(raw)) return null
    const label = stringOrNull(raw.label)
    return label ? { label, confidence: finiteOrNull(raw.confidence) } : null
  } catch {
    return null
  }
}

/**
 * VLM label read via llm-service `POST /api/v1/gemini/generate` with the
 * uploaded photo as a `rolodex_files` reference. Null on ANY failure, in
 * which case the election keeps its fallback label.
 */
export async function generatePhotoLabel(
  deps: PhotoServiceDeps,
  photoEntryId: string
): Promise<{ label: string; confidence: number | null } | null> {
  try {
    const res = await deps.axios.post<{ result: string }>(
      `${deps.llmServiceUrl}/api/v1/gemini/generate`,
      {
        content: PHOTO_LABEL_PROMPT,
        rolodex_files: [
          { id: photoEntryId, path: PHOTO_FILE_PATH, mime_type: 'image/jpeg' },
        ],
        response_schema: PHOTO_LABEL_SCHEMA,
      }
    )
    return parsePhotoLabel(res.data.result)
  } catch {
    return null
  }
}

export function usePhotoService() {
  const { ROLODEX_URL, LLM_SERVICE } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const { user } = useGeckoPermissions()
  const { rolodexPatch, rolodexDelete, rolodexUploadFile } = useRolodexEdit()

  const uploader = useMemo<PhotoUploadedBy | null>(
    () =>
      user
        ? { email: user.email, name: user.name || null, user_id: user.id }
        : null,
    [user]
  )
  const client = useMemo(photoClient, [])

  const deps = useMemo<PhotoServiceDeps>(
    () => ({
      axios: authorizedAxios,
      rolodexUrl: ROLODEX_URL,
      llmServiceUrl: LLM_SERVICE,
      rolodexPatch,
      rolodexDelete,
      rolodexUploadFile,
      uploader,
      client,
      locate: photoLocator.current,
      hashFile: sha256OfBlob,
    }),
    [
      ROLODEX_URL,
      LLM_SERVICE,
      authorizedAxios,
      rolodexPatch,
      rolodexDelete,
      rolodexUploadFile,
      uploader,
      client,
    ]
  )

  return useMemo(
    () => ({
      uploadPhoto: (siteId: string, source: PhotoSource, file: File) =>
        uploadPhoto(deps, siteId, source, file),
      retryPhotoUpload: (photoEntryId: string, file: File) =>
        retryPhotoUpload(deps, photoEntryId, file),
      discardPhoto: (photoEntryId: string) => discardPhoto(deps, photoEntryId),
      attachPhoto: (photoEntryId: string, request: AttachPhotoRequest) =>
        attachPhoto(deps, photoEntryId, request),
      detachPhoto: (photoEntryId: string, request: PhotoElectionRequest) =>
        detachPhoto(deps, photoEntryId, request),
      removePhotoFromSubject: (
        photoEntryId: string,
        request: PhotoElectionRequest
      ) => removePhotoFromSubject(deps, photoEntryId, request),
      setHeroPhoto: (photoEntryId: string, request: PhotoElectionRequest) =>
        setHeroPhoto(deps, photoEntryId, request),
      setPhotoLabel: (photoEntryId: string, label: string) =>
        setPhotoLabel(deps, photoEntryId, label),
      setPhotoCaption: (photoEntryId: string, caption: string | null) =>
        setPhotoCaption(deps, photoEntryId, caption),
      generatePhotoLabel: (photoEntryId: string) =>
        generatePhotoLabel(deps, photoEntryId),
    }),
    [deps]
  )
}
